import React, { useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "../../utils";
import { getCurrentUserDetailsActionInitiate } from "../../redux/actions/profile/getCurrentUserDetailsAction";

/**
 * DashboardRoute: Intelligent router for /dashboard
 *
 * Purpose:
 * - Wait for feature_blocks to be available in Redux
 * - Redirect to the first available feature URL from feature_blocks
 * - Show loader while waiting for API data
 * - Prevent infinite redirects
 *
 * Flow:
 * 1. User logs in → redirected to /dashboard
 * 2. /me API is in flight, feature_blocks not yet available → show loader
 * 3. /me API completes, feature_blocks available in Redux
 * 4. Redirect to first feature_blocks[0].url (e.g., /dashboard/local-feeds)
 *
 * Why this works:
 * - By redirecting from /dashboard instead of App.js,
 *   we ensure feature_blocks are fetched by the time DashboardRoute renders
 * - DashboardRoute only redirects once to the correct feature
 * - No infinite redirects because we use a ref to track if we've already redirected
 */
export const DashboardRoute = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasRedirected = useRef(false);

  const currentUser = useSelector((state) => state.getCurrentUserDetails?.data);
  const userLoading = useSelector((state) => state.getCurrentUserDetails?.loading);
  const userError = useSelector((state) => state.getCurrentUserDetails?.error);
  const featureBlocks = currentUser?.feature_blocks;

  useEffect(() => {
    if (!currentUser && !userLoading && !userError) {
      dispatch(getCurrentUserDetailsActionInitiate());
    }
  }, [currentUser, userLoading, userError, dispatch]);

  useEffect(() => {
    // Prevent multiple redirects by checking if we've already started redirecting
    if (hasRedirected.current) {
      return;
    }

    // If /me API is still loading, wait (show loader)
    if (userLoading) {
      return;
    }

    // If /me API has completed (userLoading = false) but currentUser is still null,
    // it means the API call failed. Fallback to local-feeds.
    if (!currentUser) {
      hasRedirected.current = true;
      navigate("/dashboard/local-feeds", { replace: true });
      return;
    }

    // If /me API completed and feature_blocks exist, redirect to first feature
    if (featureBlocks && featureBlocks.length > 0) {
      const firstFeatureUrl = featureBlocks[0].url;
      if (firstFeatureUrl) {
        hasRedirected.current = true;
        // Navigate to the first available feature
        // Use replace to replace the /dashboard route, not push (prevents back button issues)
        navigate(`/${firstFeatureUrl}`, { replace: true });
        return;
      }
    }

    // Fallback if feature_blocks is empty array (shouldn't happen in normal flow)
    // Redirect to local-feeds as default
    hasRedirected.current = true;
    navigate("/dashboard/local-feeds", { replace: true });
  }, [featureBlocks, userLoading, currentUser, navigate]);

  // Show loader while /me API is in flight
  if (userLoading || !featureBlocks) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        width: "100%"
      }}>
        <Loader variant="spinner" size="large" color="primary" text="Loading..." />
      </div>
    );
  }

  // If we reach here without redirecting, show a fallback loader
  // (this should rarely happen)
  return (
    <div style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100%"
    }}>
      <Loader variant="spinner" size="large" color="primary" text="Loading..." />
    </div>
  );
};

export default DashboardRoute;
